/* External */
import {
  memo, useCallback, useEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { FormattedMessage } from 'react-intl';

/* Ott-common */
import { Errors } from '@dtvgo/player-utils';

/* Theme Assets */
import logo from '~/assets/theme/svg/brand-logo.svg';
import playerErrorIcon from '~/assets/theme/svg/player-error.svg';

/* Components */
import { getJsonImage } from 'src/theme/images';
import FocusableButton from '../FocusableButton';

/* Utils */
import { useErrorMessages } from './utils';
import { useOnBackPress } from '../../utils/keyDownEvents';
import platform from '../../utils/platform';
import { getGtmPlatform } from '../../utils/googleTags/utils';

import { trackGAErrorMessageButton, trackGAErrorMessagePlayer } from '../../utils/trackGAData';

/* Styles */
import './ErrorScreen.scss';

import {
  GTM_EVENT_NAMES,
  GTM_CATEGORIES,
  GTM_SUBCATEGORIES,
  GTM_ERROR_BLOCK_CONTENT,
  GTM_PLATFORMS_ERROR_CODES,
} from '../../utils/googleTags';

import { useGtmLocalContext } from '../../providers/GtmLocalProvider';

const imageToShow = (code) => {
  if (code) {
    return getJsonImage('player-error') || playerErrorIcon;
  }
  return getJsonImage('brand-logo') || logo;
};

function ErrorScreen({
  error,
  contentTitle = '',
  exit = () => {},
  setFocus,
  sourceProvider = '',
  isFromInterstitial = false,
  isCurrentInterstitialSchedule = undefined,
  onRetry = undefined,
}) {
  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(true);
  const gtmPlatform = getGtmPlatform(platform?.aemPlatform);

  // TODO: Fix this logic: It is assumming all errors are NOT_IN_PACKAGE
  // Also it should not depend on internal data on the error.
  // GOOGLE_NOT_IN_PACKAGE should have its own code Errors.ErrorCode enum
  const isErrorNIPGooglePlay = error?.data?.response?.data?.details
    === Errors.AuthorizerErrorCode.GOOGLE_PLAY_NOT_IN_PACKAGE;
  const errorType = isErrorNIPGooglePlay
    ? GTM_ERROR_BLOCK_CONTENT.GOOGLE_PLAY
    : GTM_ERROR_BLOCK_CONTENT.NOT_IN_PACKAGE;

  const isInterstitialCurrentScheduleError = isFromInterstitial && isCurrentInterstitialSchedule;

  const errorScreenTagEvent = useCallback(({ eventName }) => {
    if (eventName) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.PLAYER_MOVIE_SERIE_LIVE_DATA,
        subCategory: GTM_SUBCATEGORIES.PLAYER.PLAYER_ERROR,
        eventName,
        data: {
          errorType,
          errorCode: GTM_PLATFORMS_ERROR_CODES[gtmPlatform],
        },
      });
    }
  }, [commonTaggingEvent, gtmPlatform, errorType]);

  const closePlayer = useCallback(() => {
    exit();
    trackGAErrorMessageButton(error);
  }, [error, exit]);

  const onButtonPress = useCallback(() => {
    errorScreenTagEvent({
      eventName: GTM_EVENT_NAMES.PLAYER.OK,
    });
    closePlayer();
  }, [closePlayer, errorScreenTagEvent]);

  useOnBackPress({
    listener: useCallback(() => {
      errorScreenTagEvent({
        eventName: GTM_EVENT_NAMES.PLAYER.CLOSE,
      });
      closePlayer();
    }, [closePlayer, errorScreenTagEvent]),
  });

  const { title, subtitle, button } = useErrorMessages(error, contentTitle);

  useEffect(() => {
    setFocus();
    if (tagsMountRef.current) {
      errorScreenTagEvent({
        eventName: GTM_EVENT_NAMES.PAGE_VIEW,
      });
    }
    tagsMountRef.current = false;
  }, [setFocus, errorScreenTagEvent]);

  useEffect(() => {
    trackGAErrorMessagePlayer({
      error,
      title,
      subtitle,
      sourceProvider,
      isFromInterstitial,
      isInterstitialCurrentScheduleError,
    });
  }, [
    sourceProvider,
    subtitle,
    title,
    isFromInterstitial,
    isInterstitialCurrentScheduleError,
    error,
  ]);

  const errorCode = error.code === Errors.ErrorCode.REFRESH_TOKEN_REVOKED;

  return (
    <div className="dtv-html5-c-error-screen">
      <div className="dtv-html5-c-error-screen__content">
        <img loading="lazy" decoding="async" className="dtv-html5-c-error-screen__logo" src={imageToShow(errorCode)} alt="dtv-logo" />
        <span className="dtv-html5-c-error-screen__title">
          {title}
        </span>
        <span className="dtv-html5-c-error-screen__subtitle">
          {subtitle}
        </span>
        <div className="dtv-html5-c-error-screen__buttons">
          {/* Optional: only callers that can actually recover pass it (the
              lego player, for technical errors). Listed first so it takes the
              initial focus. */}
          {onRetry && (
            <FocusableButton
              className="dtv-html5-c-error-screen__button"
              onEnterPress={onRetry}
              primary
            >
              <FormattedMessage id="profile.error.retry" />
            </FocusableButton>
          )}
          <FocusableButton
            className="dtv-html5-c-error-screen__button"
            onEnterPress={onButtonPress}
            primary={!onRetry}
          >
            {button || <FormattedMessage id="common.back" />}
          </FocusableButton>
        </div>
      </div>
    </div>
  );
}

ErrorScreen.propTypes = {
  error: PropTypes.shape({
    data: PropTypes.shape({
      response: PropTypes.shape({
        data: PropTypes.shape({
          details: PropTypes.string,
        }),
      }),
    }),
    code: PropTypes.string,
  }).isRequired,
  contentTitle: PropTypes.string,
  exit: PropTypes.func,
  setFocus: PropTypes.func.isRequired,
  sourceProvider: PropTypes.string,
  isFromInterstitial: PropTypes.bool,
  isCurrentInterstitialSchedule: PropTypes.bool,
  onRetry: PropTypes.func,
};

export default memo(withFocusable()(memo(ErrorScreen)));
